/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toMinWage } from '@data/dto/min-wage.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  MinWage,
  MinWageHttp,
  MinWageWizard,
} from '@data/models/min-wage.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class MinWageService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly activityFieldEndpoint = '/min-wage-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listMinWages(
    filters: FilterParams,
  ): Promise<PaginateResult<MinWage[]>> {
    const { data: response, error } = await this.API?.get<{
      data: MinWageHttp[];
      meta: Meta;
    }>(this.activityFieldEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toMinWage) || [],
      totalSize: response?.meta?.page.total || 0,
    };
  }

  public async upsertMinWage({
    id,
    ...activityFieldWizard
  }: MinWageWizard): Promise<MinWage | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<MinWageWizard, 'id'> },
        { data: MinWageHttp }
      >(this.activityFieldEndpoint, { data: activityFieldWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toMinWage(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: MinWageWizard },
      { data: MinWageHttp }
    >(`${this.activityFieldEndpoint}/${id}`, {
      data: { ...activityFieldWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toMinWage(data?.data || {});
  }

  public async deleteMinWage(id: string): Promise<MinWage | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: MinWageHttp;
    }>(`${this.activityFieldEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toMinWage(data?.data || {});
  }

  public async sortMinWage(
    sortParams: DatatableReorderParams<MinWage>,
  ): Promise<MinWage | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.activityFieldEndpoint}/${id}/sort`,
      {
        data: toSortParams('min_wage_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
